import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  KeyboardEvent as ReactKeyboardEvent,
} from "react";

type DropdownValue = string;

export interface DropdownOption<T extends DropdownValue> {
  value: T;
  label: string;
}

interface DropdownSelectProps<T extends DropdownValue> {
  options: DropdownOption<T>[];
  value: T;
  onChange: (value: T) => void;
  placeholder?: string;
  className?: string;
}

function DropdownSelect<T extends DropdownValue>({
  options,
  value,
  onChange,
  placeholder,
  className,
}: DropdownSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);

  const selectedIndex = useMemo(
    () => options.findIndex((option) => option.value === value),
    [options, value]
  );

  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null;

  const closeDropdown = (focusTrigger = false) => {
    setIsOpen(false);
    setFocusedIndex(-1);
    if (focusTrigger) {
      requestAnimationFrame(() => {
        buttonRef.current?.focus();
      });
    }
  };

  const handleOptionSelect = (optionValue: T) => {
    if (optionValue !== value) {
      onChange(optionValue);
    }
    closeDropdown(true);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        closeDropdown(false);
      }
    };

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDropdown(true);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const targetIndex = selectedIndex >= 0 ? selectedIndex : 0;
    setFocusedIndex(targetIndex);
  }, [isOpen, selectedIndex]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const element = optionRefs.current[focusedIndex];
    element?.focus();
  }, [focusedIndex, isOpen]);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleButtonKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>
  ) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setIsOpen(true);
      const target =
        selectedIndex >= 0
          ? selectedIndex
          : event.key === "ArrowUp"
          ? options.length - 1
          : 0;
      setFocusedIndex(target);
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleDropdown();
    }
  };

  const handleOptionKeyDown = (
    event: ReactKeyboardEvent<HTMLLIElement>,
    optionValue: T,
    index: number
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleOptionSelect(optionValue);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown(true);
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (options.length === 0) {
        return;
      }

      const direction = event.key === "ArrowDown" ? 1 : -1;
      const nextIndex = (index + direction + options.length) % options.length;
      setFocusedIndex(nextIndex);
    }
  };

  return (
    <div
      className={`filter-select ${className ? className : ""}`}
      ref={containerRef}
    >
      <button
        type="button"
        className={`filter-select-button ${isOpen ? "open" : ""}`}
        onClick={toggleDropdown}
        onKeyDown={handleButtonKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        ref={buttonRef}
      >
        <span className="filter-select-label">
          {selectedOption ? selectedOption.label : placeholder || "Seleziona"}
        </span>
        <span className="filter-select-arrow" aria-hidden="true" />
      </button>

      {isOpen && (
        <ul className="filter-select-options" role="listbox">
          {options.map((option, index) => (
            <li
              key={String(option.value)}
              role="option"
              aria-selected={option.value === value}
              className={`filter-select-option ${
                option.value === value ? "selected" : ""
              }`}
              tabIndex={-1}
              ref={(element) => {
                optionRefs.current[index] = element;
              }}
              onClick={() => handleOptionSelect(option.value)}
              onKeyDown={(event) =>
                handleOptionKeyDown(event, option.value, index)
              }
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default DropdownSelect;
